import { useAuth0 } from '@auth0/auth0-react'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

// Ensure path starts with /v1
function buildPath(path: string): string {
  if (API_BASE_URL.endsWith('/v1')) {
    return `${API_BASE_URL}${path}`
  }
  return `${API_BASE_URL}/v1${path}`
}

export interface Location {
  locationId: string
  name: string
  address: string
  city: string
  state: string
  zipCode: string
  phone?: string
  website?: string
  description?: string
  capacity?: number
  amenities: string[]
  coordinates?: {
    lat: number
    lng: number
  }
  isActive: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface CreateLocationData {
  name: string
  address: string
  city: string
  state: string
  zipCode: string
  phone?: string
  website?: string
  description?: string
  capacity?: number
  amenities?: string[]
  coordinates?: {
    lat: number
    lng: number
  }
  isActive?: boolean
}

export interface UpdateLocationData extends Partial<CreateLocationData> {}

class LocationsApi {
  private getAccessTokenSilently: (() => Promise<string>) | null = null

  setAuth(getAccessTokenSilently: () => Promise<string>) {
    this.getAccessTokenSilently = getAccessTokenSilently
  }

  private async getHeaders(): Promise<HeadersInit> {
    if (!this.getAccessTokenSilently) {
      throw new Error('Auth not initialized. Call setAuth() first.')
    }
    
    try {
      const token = await this.getAccessTokenSilently()
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    } catch (error) {
      throw new Error('Authentication failed')
    }
  }

  async getLocations(): Promise<{ locations: Location[]; total: number }> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(buildPath('/locations'), {
        method: 'GET',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw error
    }
  }

  async getLocation(locationId: string): Promise<Location> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(buildPath(`/locations/${locationId}`), {
        method: 'GET',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data.location
    } catch (error) {
      throw error
    }
  }

  async createLocation(locationData: CreateLocationData): Promise<Location> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(buildPath('/locations'), {
        method: 'POST',
        headers,
        body: JSON.stringify(locationData),
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data.location
    } catch (error) {
      throw error
    }
  }

  async updateLocation(locationId: string, locationData: UpdateLocationData): Promise<Location> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(buildPath(`/locations/${locationId}`), {
        method: 'PATCH',
        headers,
        body: JSON.stringify(locationData),
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data.location
    } catch (error) {
      throw error
    }
  }

  async deleteLocation(locationId: string): Promise<void> {
    try {
      const headers = await this.getHeaders()
      const response = await fetch(buildPath(`/locations/${locationId}`), {
        method: 'DELETE',
        headers,
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      throw error
    }
  }
}

export const locationsApi = new LocationsApi()

// Hook to initialize locationsApi with Auth0
export const useLocationsApi = () => {
  const { getAccessTokenSilently } = useAuth0()
  
  // Initialize the locationsApi with Auth0 token function
  locationsApi.setAuth(getAccessTokenSilently)
  
  return locationsApi
}
